export const DISPLAY_STORAGE_KEY = 'streamit.display.v1';
export const DISPLAY_SCALES = [75, 90, 100, 110, 125, 150, 175, 200] as const;
export type DisplayScale = typeof DISPLAY_SCALES[number];
export const displayScale = (value: unknown): DisplayScale =>
  typeof value === 'number' && DISPLAY_SCALES.includes(value as DisplayScale) ? value as DisplayScale : 100;

type Display = { requested: DisplayScale; effective: DisplayScale; width: number; supported: boolean; error: string };
let current: Display = { requested: 100, effective: 100, width: 1024, supported: false, error: '' };
const listeners = new Set<() => void>();
let dispose: (() => void) | undefined;
let scrollbarWidth = 0;
let viewportProbe: HTMLDivElement | undefined;
export const readDisplayPreferences = () => current;
export const subscribeDisplayPreferences = (notify: () => void) => { listeners.add(notify); return () => { listeners.delete(notify); }; };

function stored(): DisplayScale {
  try { const value = JSON.parse(localStorage.getItem(DISPLAY_STORAGE_KEY) || 'null');
    return value?.version === 1 ? displayScale(value.scale) : 100;
  } catch { return 100; }
}
function apply(requested: DisplayScale, error = current.error) {
  const root = document.documentElement;
  const supported = CSS.supports('zoom', '1.25') && CSS.supports('container-type', 'inline-size');
  // Preserve a usable layout when a desktop preference is opened in a narrow window.
  // The saved choice is retained and returns when the window has enough room.
  // Retain the largest observed gutter so a scrollbar appearing/disappearing
  // cannot oscillate between two scales at a narrow-window boundary.
  scrollbarWidth = Math.max(scrollbarWidth, innerWidth - (root.clientWidth || innerWidth));
  const available = innerWidth - scrollbarWidth;
  const maximum = DISPLAY_SCALES.filter(scale => available / (scale / 100) >= 320).at(-1) ?? 75;
  const effective = supported ? Math.min(requested, maximum) as DisplayScale : 100;
  if (supported) {
    root.dataset.displayScaling = '';
    root.style.zoom = String(effective / 100);
    // WebKit already compensates viewport units for root zoom; Chromium does
    // not. Measure the actual ratio instead of applying compensation twice.
    // A zero-height, clipped probe never contributes to scrolling or hit tests.
    if (!viewportProbe) {
      viewportProbe = document.createElement('div');
      viewportProbe.setAttribute('aria-hidden', 'true');
      viewportProbe.style.cssText = 'all:initial;position:fixed;inset:0 auto auto 0;width:100vw;height:0;contain:strict;overflow:clip;visibility:hidden;pointer-events:none';
      document.body.append(viewportProbe);
    }
    const viewportRatio = innerWidth / viewportProbe.getBoundingClientRect().width;
    const compensation = Number.isFinite(viewportRatio) && viewportRatio > 0 ? viewportRatio : 100 / effective;
    for (const unit of ['vw', 'vh', 'dvh', 'svh']) root.style.setProperty(`--display-${unit}`, `${compensation}${unit}`);
  }
  const width = root.getBoundingClientRect().width / (effective / 100);
  if (current.requested === requested && current.effective === effective && current.width === width && current.supported === supported && current.error === error) return;
  current = { requested, effective, width, supported, error };
  listeners.forEach(notify => notify());
}

export function saveDisplayScale(value: unknown) {
  startDisplayPreferences();
  const requested = displayScale(value), encoded = JSON.stringify({ version: 1, scale: requested });
  let error = '';
  try { localStorage.setItem(DISPLAY_STORAGE_KEY, encoded); if (localStorage.getItem(DISPLAY_STORAGE_KEY) !== encoded) throw new Error('storage'); }
  catch { error = 'Could not save on this device. This size lasts until you reload or close this page.'; }
  apply(requested, error);
}

/** Phone/PWA and desktop only. TV native surfaces retain their own scaling. */
export function startDisplayPreferences() {
  if (dispose || typeof window === 'undefined') return;
  const resize = () => apply(current.requested);
  const storage = (event: StorageEvent) => { if (event.key === DISPLAY_STORAGE_KEY || event.key === null) apply(stored(), ''); };
  const reset = (event: KeyboardEvent) => {
    if (event.altKey && event.shiftKey && !event.ctrlKey && !event.metaKey && event.code === 'Digit0') {
      event.preventDefault(); saveDisplayScale(100);
    }
  };
  addEventListener('resize', resize); addEventListener('storage', storage); addEventListener('keydown', reset);
  // Applying viewport compensation can change the root's height (for example
  // when Library loses its scrollbar). Do that outside observer delivery so
  // the resulting layout is measured in the next frame, without a resize loop.
  let frame = 0;
  const observer = new ResizeObserver(() => {
    if (!frame) frame = requestAnimationFrame(() => { frame = 0; resize(); });
  });
  observer.observe(document.documentElement);
  dispose = () => { removeEventListener('resize', resize); removeEventListener('storage', storage); removeEventListener('keydown', reset); observer.disconnect(); cancelAnimationFrame(frame); viewportProbe?.remove(); viewportProbe = undefined; dispose = undefined; };
  apply(stored(), '');
}
if (import.meta.hot) import.meta.hot.dispose(() => dispose?.());
